import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import useRacingFeed from '../hooks/useRacingFeed'
import useRacingFlucs from '../hooks/useRacingFlucs'
import { getUserFilters, saveUserFilter, updateUserFilter } from '../services/userDataService'
import OddsBetslip, { slipKey } from '../ODDS_PAGE/OddsBetslip'
import { bookmakerLogo } from '../config/bookmakers'
import '../styles/app.css'
import './RacingOdds.css'

// Shared by the desktop (RacingOdds.jsx) and mobile (RacingOddsMobile.jsx)
// layouts: constants, formatting, the race store, the small parts both draw,
// and useRacingBoard, which owns the feed, the races and the saved settings.

export const CODES = [
  { id: 'thoroughbred', label: 'Thoroughbred' },
  { id: 'harness', label: 'Harness' },
  { id: 'greyhound', label: 'Greyhound' },
]
export const MEETING_PREVIEW = 3              // races shown per meeting before 'show all'
export const NEXT_COUNT = 12                   // chips in the next-to-go strip
export const RUN_IN_MS = 60 * 60 * 1000        // a chip's line fills over the last hour
export const LIVE_AFTER_MS = 15 * 60 * 1000    // the API keeps a race live this long past its start
export const DROP_AFTER_MS = 2 * 60 * 1000     // a closed race leaves the lists this long after closing
export const SOON_MS = 5 * 60 * 1000           // amber from here
export const FLASH_MS = 1600
// Market movers: the biggest backed and the biggest drifting runner per race, judged on WIN chance
// (1 / median win price) so a favourite's move outweighs the same % move on a roughie.
export const MOVER_OPEN_PTS = 5                // chance moved this many points since opening...
export const MOVER_RECENT_PTS = 3              // ...or this many in the last MOVER_RECENT_MS
export const MOVER_RECENT_MS = 30 * 60 * 1000
export const MOVER_MIN_BOOKS = 5               // books with history for the runner, so one odd book can't flag it
export const MOVER_LATE_MS = 10 * 60 * 1000    // inside this of the jump, backed reads as late money
// The user's racing settings (hidden books, column order, meetings list, flucs column) live in
// ONE user_filters row with this page_type, so they follow them to any device.
export const PREFS_PAGE = 'racing'
export const PREFS_SAVE_MS = 1000                   // writes wait for a 1s pause, so a burst of changes is one write

// One logo list for the whole site (config/bookmakers.js), looked up whatever
// the casing, so a new racing book only needs adding there.
export const logoUrl = (book) => bookmakerLogo(book)

// Australian greyhound rug colours (GWIC rule R22 / GA rule 85). Reserves 9 and 10
// keep their own rug when they get a run from a scratched dog's box.
export const GREYHOUND_RUGS = {
  1: { background: '#d63a34', color: '#fff' },
  2: { background: 'repeating-linear-gradient(180deg, #14161a 0 5px, #f4f4f4 5px 10px)', color: '#d63a34' },
  3: { background: '#f4f4f4', color: '#14161a' },
  4: { background: '#2463c7', color: '#fff' },
  5: { background: '#f2c230', color: '#14161a' },
  6: { background: '#1e8a4f', color: '#fff' },
  7: { background: '#14161a', color: '#f2c230' },
  8: { background: '#ef8fb4', color: '#14161a' },
  9: { background: 'repeating-linear-gradient(180deg, #1e8a4f 0 5px, #f4f4f4 5px 10px)', color: '#14161a' },
  10: { background: 'linear-gradient(180deg, #d63a34 0 33.3%, #f4f4f4 33.3% 66.6%, #2463c7 66.6% 100%)', color: '#14161a' },
}

// ---------- formatting ----------

export const fmtPrice = (p) => {
  if (p == null) return null
  if (p < 10) return p.toFixed(2)
  return Number.isInteger(p) ? String(p) : p.toFixed(1)
}

export const fmtTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, '').toLowerCase()

// Time to the start; past it (overdue, not yet closed) reads "+1:24".
export const fmtCountdown = (ms) => {
  if (ms <= 0) {
    const over = Math.floor(-ms / 1000)
    return `+${Math.floor(over / 60)}:${String(over % 60).padStart(2, '0')}`
  }
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (ms < SOON_MS) return `${m}:${String(s % 60).padStart(2, '0')}`
  if (m < 60) return `${m}m`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

// How long ago a fluc was: "40s ago", "12m ago", "3h 5m ago".
export const fmtAgo = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  return `${Math.floor(m / 60)}h ${m % 60}m ago`
}

// "Zac Sure Can (Em1)" -> name + emergency tag
export const splitName = (raw) => {
  const match = /\s*\((em\d*)\)\s*$/i.exec(raw || '')
  return match ? { name: raw.slice(0, match.index), emergency: true } : { name: raw || '', emergency: false }
}

export const isVacant = (name) => /^vacant box$/i.test((name || '').trim())

export const median = (values) => {
  const v = [...values].sort((a, b) => a - b)
  const mid = Math.floor(v.length / 2)
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2
}

// ---------- race store ----------

export function upsertRace(map, r) {
  let race = map.get(r.race_id)
  if (!race) {
    race = { race_id: r.race_id, books: {}, abandoned: false }
    map.set(r.race_id, race)
  }
  race.code = r.code
  race.track = r.track
  race.country = r.country
  race.race_number = r.race_number
  // The API's start_time is the time most books agree on (racing-api 1.7.0), on
  // every message, so it is simply taken. Each book's own time is book_start_time.
  if (r.start_time) race.start = Date.parse(r.start_time)
  if (r.abandoned) race.abandoned = true
  markClosed(race, r.closed)
  return race
}

// closed = 2+ books shut betting at the jump (or the only book did). It only
// ever goes true; closedAt times how long the race stays in the lists after.
export function markClosed(race, closed) {
  if (closed && !race.closed) {
    race.closed = true
    race.closedAt = Date.now()
  }
}

// One bookmaker's block for a race. Price changes against what we held are
// recorded as flashes, so a snapshot (nothing held) never flashes.
export function applyBook(race, book, block, flashes) {
  const prev = race.books[book]?.runners
  const runners = {}
  const at = Date.now()
  for (const rn of block.runners || []) {
    runners[rn.number] = rn
    const old = prev?.[rn.number]
    if (!old) continue
    for (const m of ['win', 'place']) {
      if (rn[m] != null && old[m] != null && rn[m] !== old[m]) {
        flashes.set(`${race.race_id}|${book}|${rn.number}|${m}`, { dir: rn[m] > old[m] ? 'up' : 'down', at })
      }
    }
  }
  race.books[book] = {
    lastUpdate: Date.parse(block.last_update),
    url: block.url,
    status: block.status,
    runners,
  }
}

// ---------- preview sample (locked users get no ticket, so no feed) ----------

// Locked users see every racing book's column, so the breadth sells itself, but
// only this corner of prices is readable: the first rows x the first books.
export const PREVIEW_CLEAR_ROWS = 2
export const PREVIEW_CLEAR_BOOKS = 3
// Blurred in the preview? (row index, book column index)
export const previewBlur = (row, col) => !(row < PREVIEW_CLEAR_ROWS && col < PREVIEW_CLEAR_BOOKS)

export function buildPreviewRaces(now) {
  // The 18 books the racing scraper runs (Scrapers/RACINGSCRAPER racing_main_scraper.py BOOKS), named
  // exactly as each sends BOOK, so logos resolve. Keep in step when a book is added or switched off.
  const books = [
    'TAB', 'Sportsbet', 'Ladbrokes', 'Bet365', 'PointsBet', 'Betr', 'Unibet', 'Dabble', 'Picklebet',
    'BetRight', 'PalmerBet', 'NextBet', 'Amused', 'SwiftBet', 'ChaseBet', 'ColossalBet', 'EliteBet', '123bet',
  ]
  const field = [
    ['Midnight Express', 3.4], ['Golden Sovereign', 4.6], ['Northern Belle', 6.5], ['Ocean Drive', 8],
    ['Rapid Fire', 11], ['Silver Lining', 15], ['Coastal Breeze', 21], ['Last Dance', 34],
  ]
  const map = new Map()
  ;[['Randwick', 4, 4], ['Randwick', 5, 39], ['Flemington', 3, 12], ['Flemington', 4, 47], ['Doomben', 6, 26]].forEach(([track, n, mins]) => {
    const race = upsertRace(map, {
      race_id: `preview_${track}_${n}`, code: 'thoroughbred', track, country: 'AU', race_number: n,
      start_time: new Date(now + mins * 60000).toISOString(),
    })
    books.forEach((book, bi) => {
      applyBook(race, book, {
        last_update: new Date(now - 20000).toISOString(),
        url: null,
        runners: field.map(([name, base], i) => {
          const win = Math.round(base * (0.92 + ((bi * 7 + i * 3) % 10) / 60) * 20) / 20
          return { number: i + 1, name, scratched: false, win, place: Math.round((1 + (win - 1) / 3.6) * 100) / 100 }
        }),
      }, new Map())
    })
  })
  return map
}

// ---------- small parts ----------

export function RunnerMark({ number, silk, code }) {
  const [failed, setFailed] = useState(false)
  if (code === 'greyhound' && GREYHOUND_RUGS[number]) {
    return <span className="rc-mark rc-rug" style={GREYHOUND_RUGS[number]}>{number}</span>
  }
  if (silk && !failed) {
    return (
      <span className="rc-mark rc-silk">
        <img src={silk} alt="" onError={() => setFailed(true)} />
        <span className="rc-silk-num">{number}</span>
      </span>
    )
  }
  return <span className="rc-mark rc-num">{number}</span>
}

export function BookLogo({ book, size = 32 }) {
  const [failed, setFailed] = useState(false)
  const src = logoUrl(book)
  if (!src || failed) return <span className="rc-logo rc-logo-text" style={{ width: size, height: size }}>{book.slice(0, 2)}</span>
  return <img className="rc-logo" src={src} alt={book} style={{ width: size, height: size }} onError={() => setFailed(true)} />
}

// Code icons, 64x40. Body in currentColor; the rider (or the greyhound's rug)
// is .rc-ico-rider so the selected tab can colour it.
export function CodeIcon({ code }) {
  if (code === 'thoroughbred') {
    return (
      <svg className="rc-ico" viewBox="0 0 64 40" aria-hidden="true">
        <path d="M14 20 C14 16 18 14 24 14 L36 14 C40 14 42 12 45 9 L50 5 C52 4 54 4 55 5 L56 3 L57 6 C59 8 61 11 62 13 C62 15 60 15 58 14 L52 13 C49 15 47 18 46 21 C46 24 44 26 41 26 L26 27 C20 27 15 25 14 20 Z" />
        <path d="M42 24 L52 29 L58 28 L59 30 L51 32 L39 27 Z" />
        <path d="M37 26 L42 31 L47 33 L46 35 L40 33 L34 27 Z" />
        <path d="M18 22 L8 29 L3 30 L3 32 L10 32 L22 25 Z" />
        <path d="M24 25 L20 32 L22 36 L24 36 L23 32 L28 26 Z" />
        <path d="M15 17 C10 15 5 17 2 22 C6 20 10 20 15 21 Z" />
        <g className="rc-ico-rider">
          <circle cx="40" cy="5.5" r="2.6" />
          <path d="M30 13 C32 9 36 7.5 40 8.5 L41 10.5 C37 10.5 34 12 33 14 Z" />
          <path d="M39 9.5 L47 10 L47 11.8 L39 11.5 Z" />
        </g>
      </svg>
    )
  }
  if (code === 'harness') {
    return (
      <svg className="rc-ico" viewBox="0 0 64 40" aria-hidden="true">
        <g transform="translate(14 4) scale(0.78)">
          <path d="M14 20 C14 16 18 14 24 14 L36 14 C40 14 42 12 45 9 L48 3 C50 1 52 1 53 2 L54 0 L55 3 C57 5 59 8 60 11 C60 13 58 13 56 12 L51 11 C48 14 47 18 46 21 C46 24 44 26 41 26 L26 27 C20 27 15 25 14 20 Z" />
          <path d="M42 25 L48 31 L50 38 L48 38 L46 32 L39 27 Z" />
          <path d="M36 26 L34 33 L37 38 L35 38 L31 33 L33 26 Z" />
          <path d="M18 23 L22 31 L20 38 L18 38 L19 31 L15 24 Z" />
          <path d="M25 25 L31 31 L34 36 L32 36 L28 32 L22 27 Z" />
          <path d="M15 17 C11 17 9 21 9 27 C11 23 13 21 15 21 Z" />
        </g>
        <circle cx="9" cy="30" r="7.5" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="30" r="1.6" />
        <path d="M9 30 L5 22 L19 21 L30 20 L30 21.6 L18 23 L8 23.5 Z" />
        <g className="rc-ico-rider">
          <circle cx="8" cy="11" r="2.6" />
          <path d="M4 21 C4 16 6 14 9 14 C11 14 12 16 12 18 L16 17 L22 18 L22 19.6 L15 19.6 L12 21 Z" />
        </g>
      </svg>
    )
  }
  return (
    <svg className="rc-ico" viewBox="0 0 64 40" aria-hidden="true">
      <path d="M12 16 C16 11 24 10 30 12 C35 13.5 40 13 44 12 C47 11 49 9 51 7 C53 5.5 56 5 58 6 L63 9 L62 10.5 C59 10.5 56 10.5 54 11.5 C52 13 51 15 50 17 C49 21 47 25 43 26 C40 26.5 37 24 34 20 C31 18 28 18 25 19.5 C21 21 15 21 12 16 Z" />
      <path d="M54.5 6.2 L51 3.8 L52.6 7.4 Z" />
      <path d="M46 22 L55 27 L61 27.5 L61 29.5 L54 29.5 L43 25 Z" />
      <path d="M42 25 L50 31 L54 34 L53 35.5 L48 33 L40 26 Z" />
      <path d="M17 18 L8 25 L2 26.5 L2 28.5 L9 28 L21 20 Z" />
      <path d="M20 20 L15 28 L17 32 L19 32 L18 28 L24 21 Z" />
      <path d="M12.5 15.5 C8 14 5 17 4 21 C3 24 2 26 0.5 27.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path className="rc-ico-rider" d="M28 12.6 C33 13.8 38 13.4 41 12.8 L39.5 21.5 C37 19.5 35 19 33.5 19.2 C31 18.4 29.5 18.4 27 19 Z" />
    </svg>
  )
}

export function Segmented({ options, value, onChange, label, icons }) {
  return (
    <div className={`rc-seg${icons ? ' rc-seg-icons' : ''}`} role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.id}
          role="tab"
          aria-selected={value === o.id}
          aria-label={icons ? o.label : undefined}
          data-tip={icons ? o.label : undefined}
          className="rc-seg-btn"
          onClick={() => onChange(o.id)}
        >
          {icons ? <CodeIcon code={o.id} /> : o.label}
        </button>
      ))}
    </div>
  )
}

export function BookFilter({ books, hidden, onChange, customOrder, onResetOrder, onReorder }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const listRef = useRef(null)
  // Reorder by dragging a row's grip: { book, from, over, dy, h }. Pointer events
  // cover mouse and touch; the grip has touch-action none so the page doesn't scroll.
  const [drag, setDrag] = useState(null)
  const dragRef = useRef(null)
  const pointerY = useRef(0)
  const rafRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    const esc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  useEffect(() => () => cancelAnimationFrame(rafRef.current), [])

  const shown = books.filter((b) => !hidden.has(b)).length
  const filtered = shown < books.length
  const toggle = (b) => {
    const next = new Set(hidden)
    next.has(b) ? next.delete(b) : next.add(b)
    onChange(next)
  }

  // Where the dragged row sits now: pointer travel plus however far the list
  // has auto-scrolled since the press.
  const update = () => {
    const d = dragRef.current
    const list = listRef.current
    if (!d || !list) return
    const dy = pointerY.current - d.startY + (list.scrollTop - d.startScroll)
    const over = Math.max(0, Math.min(books.length - 1, d.from + Math.round(dy / d.h)))
    dragRef.current = { ...d, dy, over }
    setDrag(dragRef.current)
  }
  // While dragging, nudge the list when the pointer is near its top or bottom edge.
  const autoScroll = () => {
    const list = listRef.current
    if (!dragRef.current || !list) return
    const r = list.getBoundingClientRect()
    const edge = 44
    const y = pointerY.current
    const step = y < r.top + edge ? -Math.ceil((r.top + edge - y) / 6) : y > r.bottom - edge ? Math.ceil((y - (r.bottom - edge)) / 6) : 0
    if (step) {
      list.scrollTop += step
      update()
    }
    rafRef.current = requestAnimationFrame(autoScroll)
  }
  const startDrag = (e, book, index) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const row = e.currentTarget.closest('.rc-filter-item')
    const gap = 2
    pointerY.current = e.clientY
    dragRef.current = { book, from: index, over: index, dy: 0, h: (row?.offsetHeight || 40) + gap, startY: e.clientY, startScroll: listRef.current?.scrollTop || 0 }
    setDrag(dragRef.current)
    rafRef.current = requestAnimationFrame(autoScroll)
  }
  const moveDrag = (e) => {
    if (!dragRef.current) return
    pointerY.current = e.clientY
    update()
  }
  const endDrag = () => {
    const d = dragRef.current
    cancelAnimationFrame(rafRef.current)
    dragRef.current = null
    setDrag(null)
    if (d && d.over !== d.from) {
      const next = books.filter((b) => b !== d.book)
      next.splice(d.over, 0, d.book)
      onReorder(next)
    }
  }
  // Rows between the dragged row's start and its target slide one slot to make room.
  const shiftFor = (i) => {
    if (!drag || i === drag.from) return 0
    if (drag.from < drag.over && i > drag.from && i <= drag.over) return -drag.h
    if (drag.from > drag.over && i < drag.from && i >= drag.over) return drag.h
    return 0
  }

  return (
    <div className="rc-filter" ref={ref}>
      <button className={`rc-filter-btn${filtered ? ' is-filtered' : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Bookmakers
        {filtered && <span className="rc-filter-count">{shown} of {books.length}</span>}
        <svg className="rc-filter-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="rc-filter-pop" role="dialog" aria-label="Bookmakers">
          <div className="rc-filter-top">
            <span className="rc-filter-title">Bookmakers</span>
            <button className="rc-filter-link" onClick={() => onChange(new Set())} disabled={!filtered}>Select all</button>
            <button className="rc-filter-link" onClick={() => onChange(new Set(books))} disabled={shown === 0}>Clear</button>
          </div>
          <div className="rc-filter-order">
            {customOrder ? 'Your own order. Drag the grips to change it.' : 'Drag the grips to set your column order.'}
            {customOrder && <button className="rc-filter-link" onClick={onResetOrder}>Reset</button>}
          </div>
          <div className={`rc-filter-list${drag ? ' is-dragging' : ''}`} ref={listRef}>
            {books.map((b, i) => {
              const on = !hidden.has(b)
              const isDragged = drag?.book === b
              return (
                <div
                  key={b}
                  className={`rc-filter-item${on ? ' is-on' : ''}${isDragged ? ' is-dragged' : ''}`}
                  style={{ transform: `translateY(${isDragged ? drag.dy : shiftFor(i)}px)` }}
                >
                  <button className="rc-filter-toggle" role="checkbox" aria-checked={on} onClick={() => toggle(b)}>
                    <BookLogo book={b} size={22} />
                    <span className="rc-filter-name">{b}</span>
                    <span className="rc-filter-check" aria-hidden="true">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                  </button>
                  <span
                    className="rc-filter-grip"
                    aria-label={`Move ${b}`}
                    onPointerDown={(e) => startDrag(e, b, i)}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                    </svg>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Thin next-to-go strip under the header. Each chip's line fills over the
// race's last hour, empty at 1h out and full at the jump.
export function NextToGo({ races, now, selectedId, onSelect }) {
  const list = races.slice(0, NEXT_COUNT)
  if (!list.length) return null
  return (
    <nav className="rc-ntg" aria-label="Next to go">
      {list.map((r) => {
        const left = r.start - now
        const fill = Math.max(0, Math.min(1, 1 - left / RUN_IN_MS))
        const cls = ['rc-ntg-chip', left < SOON_MS && 'is-soon', r.race_id === selectedId && 'is-selected'].filter(Boolean).join(' ')
        return (
          <button key={r.race_id} className={cls} onClick={() => onSelect(r.race_id)} data-tip={`Starts ${fmtTime(r.start)}`}>
            <span className="rc-ntg-name">{r.track} R{r.race_number}</span>
            <span className="rc-ntg-cd">{fmtCountdown(left)}</span>
            <span className="rc-ntg-line"><span style={{ transform: `scaleX(${fill})` }} /></span>
          </button>
        )
      })}
    </nav>
  )
}


// ---------- flucs ----------

// One price history as moves: repeats dropped (a snapshot that saw the same
// price adds nothing), then the live price if it has moved since the last
// snapshot, since flucs can lag the feed. Points read `points[i][key]`.
export function priceMoves(points, key, live, liveAt) {
  const moves = []
  for (const p of points || []) {
    const price = p[key]
    if (price == null || price === moves[moves.length - 1]?.price) continue
    moves.push({ t: p.t, price })
  }
  if (live != null && live !== moves[moves.length - 1]?.price) {
    moves.push({ t: Number.isFinite(liveAt) ? liveAt : Date.now(), price: live, live: true })
  }
  return moves
}

// A price over time as a step line (a price holds until it moves) on a real
// time axis, since snapshots get closer together near the jump. Runs on to
// `now`. Green if it ended higher than it started, red if lower. `detail`
// (the hover chart) adds a faint fill under the line and a dot on every move.
export function FlucLine({ moves, now, width = 86, height = 14, detail = false }) {
  if (!moves || moves.length < 2) return null
  const t0 = moves[0].t
  const t1 = Math.max(now, moves[moves.length - 1].t)
  const prices = moves.map((m) => m.price)
  const lo = Math.min(...prices)
  const hi = Math.max(...prices)
  const pad = detail ? 3 : 1.5
  const x = (t) => pad + (t1 > t0 ? (t - t0) / (t1 - t0) : 1) * (width - pad * 2)
  const y = (p) => (hi > lo ? pad + (1 - (p - lo) / (hi - lo)) * (height - pad * 2) : height / 2)
  let d = `M${x(t0).toFixed(1)} ${y(moves[0].price).toFixed(1)}`
  for (const m of moves.slice(1)) d += ` H${x(m.t).toFixed(1)} V${y(m.price).toFixed(1)}`
  d += ` H${x(t1).toFixed(1)}`
  const last = moves[moves.length - 1].price
  const dir = last > moves[0].price ? 'up' : last < moves[0].price ? 'down' : 'flat'
  return (
    <svg className={`rc-fluc-line is-${dir}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      {detail && <path className="rc-fluc-area" d={`${d} V${height} H${x(t0).toFixed(1)} Z`} />}
      <path d={d} />
      {detail && moves.slice(0, -1).map((m) => <circle key={m.t} className="rc-fluc-dot" cx={x(m.t)} cy={y(m.price)} r="1.6" />)}
      <circle cx={x(t1)} cy={y(last)} r={detail ? 2.6 : 1.8} />
    </svg>
  )
}

// Opening → previous → now (opening → now with only two). Now is coloured by
// its last move, the sparkline under it by the whole run.
export function FlucMove({ moves }) {
  const last = moves[moves.length - 1]
  const prev = moves[moves.length - 2]
  const shown = moves.length > 2 ? [moves[0], prev] : [moves[0]]
  return (
    <span className="rc-fluc-move">
      {shown.map((m) => (
        <React.Fragment key={`${m.t}-${m.price}`}>
          {fmtPrice(m.price)}
          <span className="rc-fluc-to">›</span>
        </React.Fragment>
      ))}
      <span className={`rc-fluc-now is-${last.price > prev.price ? 'up' : 'down'}`}>{fmtPrice(last.price)}</span>
    </span>
  )
}

// The price a move list stood at, at time t (its last move at or before t).
const priceAt = (moves, t) => {
  let p = null
  for (const m of moves) {
    if (m.t > t) break
    p = m.price
  }
  return p
}

// How far a runner's win chance has moved, in points, over the stronger of
// two windows: since opening, or the last MOVER_RECENT_MS. Positive = backed.
// Null when it doesn't clear the bar for either.
function moverFor(line, now) {
  if (!line || line.length < 2) return null
  const to = line[line.length - 1].price
  const pts = (from) => (from ? (1 / to - 1 / from) * 100 : 0)
  const open = { from: line[0].price, recent: false }
  const ago = priceAt(line, now - MOVER_RECENT_MS)
  const recent = ago != null && ago !== line[0].price ? { from: ago, recent: true } : null
  const options = [
    { ...open, pts: pts(open.from), bar: MOVER_OPEN_PTS },
    ...(recent ? [{ ...recent, pts: pts(recent.from), bar: MOVER_RECENT_PTS }] : []),
  ].filter((o) => Math.abs(o.pts) >= o.bar)
  if (!options.length) return null
  // the window that moved furthest past its own bar
  const pick = options.sort((a, b) => Math.abs(b.pts) / b.bar - Math.abs(a.pts) / a.bar)[0]
  return { from: pick.from, to, pts: pick.pts, recent: pick.recent }
}

// Small trend mark beside a runner's name: amber = backed, cool blue = drifting.
// Not green/red: on this page those mean a PRICE went up/down, and a backed
// runner's price falls. The hover text (desktop data-tip) says what happened.
export function MoverMark({ mover }) {
  if (!mover) return null
  const backed = mover.kind === 'backed'
  const label = `${backed ? (mover.late ? 'Late money' : 'Backed') : 'Drifting'}: ${fmtPrice(mover.from)} to ${fmtPrice(mover.to)} ${mover.recent ? 'in the last 30 minutes' : 'since opening'}, across ${mover.books} bookmakers`
  return (
    <span className={`rc-mover is-${mover.kind}`} data-tip={label} aria-label={label} role="img">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {backed
          ? <><polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" /></>
          : <><polyline points="3 7 9 13 13 9 21 17" /><polyline points="15 17 21 17 21 11" /></>}
      </svg>
    </span>
  )
}

// Runners for one race as shown: union by number (the same at every bookmaker),
// scratched if any bookmaker has scratched it, vacant boxes dropped. Each active
// runner gets its prices for `books` in `market`, the best of them, and a market
// chance from the median win price across ALL books.
// With `flucs` (useRacingFlucs) for this race, each runner also gets `moves`
// per shown book, and `flucLine`: the median across ALL books over time. The
// race's biggest backed and biggest drifting runner get `mover` (MoverMark).
export function buildRunners(race, books, market, sortByPrice, flucs) {
  const fl = flucs?.raceId === race.race_id && !race.abandoned ? flucs : null
  const byNumber = new Map()
  for (const b of Object.keys(race.books)) {
    for (const rn of Object.values(race.books[b].runners)) {
      const cur = byNumber.get(rn.number) || { number: rn.number, rawName: rn.name, scratched: false, silk: null }
      if (rn.scratched) cur.scratched = true
      if (!cur.silk && (rn.silk_url || rn.silk)) cur.silk = rn.silk_url || rn.silk
      byNumber.set(rn.number, cur)
    }
  }
  const all = [...byNumber.values()].filter((r) => !isVacant(r.rawName))
  const scratched = all.filter((r) => r.scratched).sort((a, b) => a.number - b.number)
  let active = all.filter((r) => !r.scratched)
  for (const rn of active) {
    const { name, emergency } = splitName(rn.rawName)
    rn.name = name
    rn.emergency = emergency
    rn.prices = books.map((b) => (race.abandoned ? null : race.books[b].runners[rn.number]?.[market] ?? null))
    // Best comes from books still taking bets; a closed book's price is final
    // but can't be had. Once every shown book has closed, show the best final.
    const anyOpen = books.some((b) => race.books[b].status !== 'closed')
    const valid = rn.prices
      .filter((p, i) => p != null && (!anyOpen || race.books[books[i]].status !== 'closed'))
      .sort((a, b) => b - a)
    rn.best = valid[0] ?? null
    rn.bestBooks = rn.best == null ? [] : books.filter((b, i) => rn.prices[i] === rn.best && (!anyOpen || race.books[b].status !== 'closed'))
    const wins = Object.values(race.books).map((bk) => bk.runners[rn.number]?.win).filter((p) => p != null)
    rn.fair = wins.length ? 1 / median(wins) : 0
    if (fl) {
      rn.moves = books.map((b, i) => priceMoves(fl.byKey.get(`${b}|${rn.number}`), market, rn.prices[i], race.books[b].lastUpdate))
      const live = Object.values(race.books).map((bk) => bk.runners[rn.number]?.[market]).filter((p) => p != null)
      rn.flucLine = priceMoves(fl.median[market].get(rn.number), 'price', live.length ? median(live) : null, Date.now())
      // Movers always judge the WIN market, whichever market is on screen.
      const winLine = priceMoves(fl.median.win.get(rn.number), 'price', wins.length ? median(wins) : null, Date.now())
      const withHistory = Object.keys(race.books).filter((b) => fl.byKey.has(`${b}|${rn.number}`)).length
      rn.mover = null
      rn.moverScore = withHistory >= MOVER_MIN_BOOKS ? moverFor(winLine, Date.now()) : null
      if (rn.moverScore) rn.moverScore.books = withHistory
    }
  }
  if (fl) {
    // Only the top one each way per race, so the mark stays meaningful.
    const scored = active.filter((r) => r.moverScore)
    const backed = scored.filter((r) => r.moverScore.pts > 0).sort((a, b) => b.moverScore.pts - a.moverScore.pts)[0]
    const drifter = scored.filter((r) => r.moverScore.pts < 0).sort((a, b) => a.moverScore.pts - b.moverScore.pts)[0]
    const late = race.start - Date.now() < MOVER_LATE_MS
    if (backed) backed.mover = { ...backed.moverScore, kind: 'backed', late }
    if (drifter) drifter.mover = { ...drifter.moverScore, kind: 'drifting', late: false }
  }
  const fairTotal = active.reduce((s, r) => s + r.fair, 0) || 1
  for (const rn of active) rn.chance = rn.fair / fairTotal
  active = [...active].sort(sortByPrice
    ? (a, b) => (a.best ?? Infinity) - (b.best ?? Infinity) || a.number - b.number
    : (a, b) => a.number - b.number)
  return { active, scratched }
}

// ---------- betslip (the Sports Odds one, shared) ----------

const CODE_SPORT = { thoroughbred: 'Horse Racing', harness: 'Harness Racing', greyhound: 'Greyhounds' }

// One price on the board as a betslip selection, in the shape the Sports Odds
// slip takes. Saved as a `racing_odds` bet: its own option under "Other" in the
// tracker, kept out of EV stats. race_id + runner_number match the racing
// results feed exactly, so these can settle off race_result later (no guessing
// from track names). They settle by hand until then.
export function racingLeg(race, book, rn, market, price) {
  const key = market === 'win' ? 'racing_win' : 'racing_place'
  return {
    sport: CODE_SPORT[race.code] || 'Racing',
    match: `${race.track} R${race.race_number}`,
    market_type: key,
    base_market_type: key,
    selection: `${rn.number}. ${rn.name}`,
    bookmaker: book,
    odds: price,
    // Naive UTC like every other commence_time the tracker reads.
    commence_time: new Date(race.start).toISOString().slice(0, 19),
    side: null,
    point: null,
    player_name: null,
    team_name: null,
    // For the slip's picture only; the slip saves an explicit field list, so these stay off the bet.
    runner_number: rn.number,
    silk_url: rn.silk,
    racing_code: race.code,
    race_id: race.race_id,
    racing_market: market,
  }
}

export function RacingSlip({ slip, compact = false }) {
  return (
    <>
      <OddsBetslip
        legs={slip.legs}
        onRemove={slip.remove}
        onOddsChange={slip.setOdds}
        onClear={slip.clear}
        singlesOnly
        betTag="racing_odds"
        extraFields={(leg) => ({
          race_id: leg.race_id,
          runner_number: leg.runner_number,
          racing_code: leg.racing_code,
          racing_market: leg.racing_market,
        })}
        renderAvatar={(leg) => (
          <span className="rc-slip-mark">
            <RunnerMark number={leg.runner_number} silk={leg.silk_url} code={leg.racing_code} />
          </span>
        )}
        onSaved={(mode, count) => slip.setToast(
          mode === 'multi' ? 'Multi added to tracker' : `${count} bet${count === 1 ? '' : 's'} added to tracker`
        )}
        compact={compact}
      />
      {slip.toast && (
        <div
          onAnimationEnd={() => slip.setToast(null)}
          className="odds-slip-toast"
          style={{
            position: 'fixed', right: compact ? 8 : 24, left: compact ? 8 : 'auto', bottom: compact ? 72 : 24, zIndex: 61,
            padding: '11px 16px', borderRadius: 10, textAlign: 'center',
            border: '1px solid rgba(34,211,238,0.4)', backgroundColor: '#0b1322',
            color: '#67e8f9', fontSize: 12.5, fontWeight: 700,
          }}
        >
          {slip.toast}
        </div>
      )}
    </>
  )
}

// A book's logo that opens that race on the bookmaker's site, when it has a link.
// `tip` replaces the hover text (desktop HoverTip reads data-tip).
export function BookLink({ book, url, size, className, tip }) {
  const logo = <BookLogo book={book} size={size} />
  if (!url) return <span className={className} data-tip={tip}>{logo}</span>
  return (
    <a className={`rc-book-link${className ? ` ${className}` : ''}`} href={url} target="_blank" rel="noopener noreferrer"
      draggable={false} data-tip={tip || `Open ${book}`} onClick={(e) => e.stopPropagation()}>
      {logo}
    </a>
  )
}

// ---------- the board: feed, races, settings ----------

// withFlucs: also fetch the open race's price history (desktop for now).
export function useRacingBoard({ isVisible, isPreview, withFlucs = false }) {
  const { user, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('racing_odds')

  const [code, setCode] = useState('thoroughbred')
  const [market, setMarket] = useState('win')
  const [sortByPrice, setSortByPrice] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [hiddenBooks, setHiddenBooks] = useState(() => new Set())
  const [now, setNow] = useState(Date.now())
  const [openMeetings, setOpenMeetings] = useState(() => new Set())
  const [bookOrder, setBookOrder] = useState([])
  const [railHidden, setRailHidden] = useState(false)
  const [flucsHidden, setFlucsHidden] = useState(false)   // desktop Flucs column
  const [mobileView, setMobileView] = useState('cards')   // mobile: 'cards' or 'grid'
  // The code whose snapshot has landed. Until it matches `code` the page is
  // loading (skeleton), so an empty list only ever means "really no races".
  const [loadedCode, setLoadedCode] = useState(null)

  // ---- saved settings (Supabase) ----
  const prefs = useRef({ hiddenBooks: [], bookOrder: [], railHidden: false, flucsHidden: false, mobileView: 'cards' })
  const prefsRowId = useRef(null)
  const prefsLoaded = useRef(false)
  const prefsTouched = useRef(false)          // changed before the load landed: keep the user's change
  const prefsTimer = useRef(null)
  const prefsChain = useRef(Promise.resolve())   // saves run one at a time, so the row is only inserted once

  useEffect(() => {
    if (!user || isPreview || prefsLoaded.current) return
    prefsLoaded.current = true
    getUserFilters(user.id, PREFS_PAGE).then(({ data }) => {
      const row = data?.[0]
      if (!row) return
      prefsRowId.current = row.id
      if (prefsTouched.current) return
      const p = row.filter_data || {}
      prefs.current = { ...prefs.current, ...p }
      if (Array.isArray(p.hiddenBooks)) setHiddenBooks(new Set(p.hiddenBooks))
      if (Array.isArray(p.bookOrder)) setBookOrder(p.bookOrder)
      if (typeof p.railHidden === 'boolean') setRailHidden(p.railHidden)
      if (typeof p.flucsHidden === 'boolean') setFlucsHidden(p.flucsHidden)
      if (p.mobileView === 'cards' || p.mobileView === 'grid') setMobileView(p.mobileView)
    })
  }, [user, isPreview])

  const savePrefs = (patch) => {
    prefsTouched.current = true
    prefs.current = { ...prefs.current, ...patch }
    if (!user || isPreview) return
    clearTimeout(prefsTimer.current)
    prefsTimer.current = setTimeout(() => {
      const data = prefs.current
      prefsChain.current = prefsChain.current.then(async () => {
        if (prefsRowId.current) {
          await updateUserFilter(prefsRowId.current, { filter_data: data })
        } else {
          const { data: row } = await saveUserFilter(user.id, PREFS_PAGE, 'Racing settings', data)
          if (row) prefsRowId.current = row.id
        }
      })
    }, PREFS_SAVE_MS)
  }
  useEffect(() => () => clearTimeout(prefsTimer.current), [])

  const changeBookOrder = (next) => {
    setBookOrder(next)
    savePrefs({ bookOrder: next })
  }
  // Saved order first; books not in it (new ones) follow alphabetically.
  const orderBooks = (list) => {
    const pos = new Map(bookOrder.map((b, i) => [b, i]))
    const at = (b) => (pos.has(b) ? pos.get(b) : Infinity)
    return [...list].sort((a, b) => (at(a) - at(b)) || a.localeCompare(b, undefined, { sensitivity: 'base' }))
  }
  const changeMobileView = (v) => {
    setMobileView(v)
    savePrefs({ mobileView: v })
  }
  const toggleRail = () => {
    const next = !railHidden
    setRailHidden(next)
    savePrefs({ railHidden: next })
  }
  const toggleFlucs = () => {
    const next = !flucsHidden
    setFlucsHidden(next)
    savePrefs({ flucsHidden: next })
  }
  const toggleMeeting = (key) => setOpenMeetings((prev) => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })
  const [, setVersion] = useState(0)

  const racesRef = useRef(new Map())
  const flashesRef = useRef(new Map())
  const codeRef = useRef(code)
  codeRef.current = code
  const bumpTimer = useRef(null)
  const previewRaces = useMemo(() => (isPreview ? buildPreviewRaces(Date.now()) : null), [isPreview])

  // Many updates can land in the same instant; repaint at most every 150ms.
  const bump = () => {
    if (bumpTimer.current) return
    bumpTimer.current = setTimeout(() => {
      bumpTimer.current = null
      setVersion((v) => v + 1)
    }, 150)
  }
  useEffect(() => () => clearTimeout(bumpTimer.current), [])

  const subscriptions = useMemo(() => [{ code, tracks: '*' }], [code])

  const status = useRacingFeed({
    enabled: isVisible && !isPreview,
    subscriptions,
    onMessage: (msg) => {
      const map = racesRef.current
      if (msg.event === 'subscribed') {
        if (msg.subscription.code !== codeRef.current) return
        for (const [id, r] of map) if (r.code === msg.subscription.code) map.delete(id)
        for (const r of msg.races || []) {
          const race = upsertRace(map, r)
          for (const b of r.bookmakers || []) applyBook(race, b.bookmaker, b, flashesRef.current)
        }
        setLoadedCode(msg.subscription.code)
        bump()
      } else if (msg.event === 'race_update') {
        if (msg.code !== codeRef.current) return
        const race = upsertRace(map, msg)
        applyBook(race, msg.bookmaker, msg, flashesRef.current)
        bump()
      } else if (msg.event === 'race_closed') {
        const race = map.get(msg.race_id)
        if (race) {
          markClosed(race, true)
          bump()
        }
      } else if (msg.event === 'race_abandoned') {
        const race = map.get(msg.race_id)
        if (race) {
          race.abandoned = true
          bump()
        }
      }
    },
  })

  // Clock for countdowns, flashes and the jump line. Also prunes jumped races.
  useEffect(() => {
    if (!isVisible) return
    const id = setInterval(() => {
      const t = Date.now()
      setNow(t)
      for (const [k, f] of flashesRef.current) if (t - f.at > FLASH_MS) flashesRef.current.delete(k)
      // Gone from the store a while after the API stops sending it (or 10 min
      // after closing), so an open jumped race can still be looked at briefly.
      for (const [rid, r] of racesRef.current) {
        if (r.start < t - LIVE_AFTER_MS - 5 * 60 * 1000 || (r.closedAt && t - r.closedAt > 10 * 60 * 1000)) racesRef.current.delete(rid)
      }
    }, 1000)
    return () => clearInterval(id)
  }, [isVisible])

  const changeCode = (c) => {
    if (c === code) return
    racesRef.current.clear()
    flashesRef.current.clear()
    setSelectedId(null)
    setLoadedCode(null)
    setCode(c)
  }

  const changeHiddenBooks = (next) => {
    setHiddenBooks(next)
    savePrefs({ hiddenBooks: [...next] })
  }

  const source = previewRaces || racesRef.current
  const allRaces = [...source.values()].filter((r) => r.code === code).sort((a, b) => a.start - b.start || a.track.localeCompare(b.track))
  // Live = still taking bets: not closed, and inside the API's window (an
  // overdue race that hasn't jumped yet stays live). Abandoned races stay listed
  // until their start. `upcoming` also keeps a race for a moment after it closes.
  const isLive = (r) => !r.closed && (r.abandoned ? r.start > now : r.start > now - LIVE_AFTER_MS)
  const ahead = allRaces.filter(isLive)
  const upcoming = allRaces.filter((r) => isLive(r) || (r.closedAt && now - r.closedAt < DROP_AFTER_MS))

  // Open the next race to jump when nothing (or a pruned race) is selected.
  const selected = source.get(selectedId) || null
  useEffect(() => {
    if (!selected && ahead.length) setSelectedId(ahead[0].race_id)
  }, [selected, ahead.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const flucs = useRacingFlucs({ race: selected, enabled: withFlucs && isVisible && !isPreview })

  const meetings = useMemo(() => {
    const groups = new Map()
    for (const r of ahead) {
      const key = `${r.track}|${r.country}`
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(r)
    }
    return [...groups.values()].map((list) => list.sort((a, b) => a.race_number - b.race_number))
  }, [ahead.map((r) => r.race_id).join()]) // eslint-disable-line react-hooks/exhaustive-deps

  const bookSet = new Set()
  for (const r of allRaces) for (const b of Object.keys(r.books)) bookSet.add(b)
  const allBooks = orderBooks(bookSet)
  // Drop `book` before/after `target`, saving the full order so it holds on every race.
  const moveBook = (book, target, side) => {
    const next = allBooks.filter((b) => b !== book)
    const i = next.indexOf(target)
    if (i < 0) return
    next.splice(side === 'after' ? i + 1 : i, 0, book)
    changeBookOrder(next)
  }

  const loading = !isPreview && loadedCode !== code

  // Betslip: click a price to add it, click again to take it out. Kept across
  // races and codes, so a multi can span meetings.
  const [slipLegs, setSlipLegs] = useState([])
  const [slipToast, setSlipToast] = useState(null)
  const slipKeys = useMemo(() => new Set(slipLegs.map(slipKey)), [slipLegs])
  const slip = {
    legs: slipLegs,
    has: (leg) => slipKeys.has(slipKey(leg)),
    toggle: (leg) => {
      if (isPreview) return
      const k = slipKey(leg)
      setSlipLegs((prev) => (prev.some((l) => slipKey(l) === k) ? prev.filter((l) => slipKey(l) !== k) : [...prev, leg]))
    },
    remove: (k) => setSlipLegs((prev) => prev.filter((l) => slipKey(l) !== k)),
    setOdds: (k, v) => setSlipLegs((prev) => prev.map((l) => (slipKey(l) === k ? { ...l, odds: v } : l))),
    clear: () => setSlipLegs([]),
    toast: slipToast,
    setToast: setSlipToast,
  }

  return {
    isPreview, hadTrial, status, loading, slip,
    code, changeCode, market, setMarket, sortByPrice, setSortByPrice,
    selectedId, setSelectedId, selected, now,
    hiddenBooks, changeHiddenBooks, bookOrder, changeBookOrder, orderBooks, allBooks, moveBook,
    railHidden, toggleRail, flucsHidden, toggleFlucs, openMeetings, toggleMeeting, mobileView, changeMobileView,
    flashes: flashesRef.current, flucs, allRaces, upcoming, ahead, meetings,
  }
}
